<template>
    <auth-box class="vx-max-w-[480px]" :errors="errors">
        <div id="register-form" class="vx-flex vx-flex-col vx-gap-4">
            <div>
                <vx-label> Name </vx-label>
                <vx-input
                    v-model="data.name"
                    data-test="name"
                    autofocus
                    name="name"
                    id="name"
                    size="lg"
                    :error="displayFirstError(v$.name.$errors)"
                >
                </vx-input>
            </div>
            <div>
                <vx-label> Email </vx-label>
                <vx-input
                    :modelValue="data.email"
                    :disabled="true"
                    data-test="email"
                    type="email"
                    name="email"
                    id="email"
                    size="lg"
                    :error="displayFirstError(v$.email.$errors)"
                >
                </vx-input>
            </div>
            <div>
                <vx-label> Password </vx-label>
                <vx-input
                    type="password"
                    v-model="data.password"
                    data-test="password"
                    name="password"
                    id="password"
                    size="lg"
                    :error="displayFirstError(v$.password.$errors)"
                ></vx-input>
            </div>

            <div>
                <vx-label> Confirm Password </vx-label>
                <vx-input
                    type="password"
                    v-model="data.password_confirmation"
                    data-test="password_confirmation"
                    name="password_confirmation"
                    id="password_confirmation"
                    size="lg"
                    :error="displayFirstError(v$.password_confirmation.$errors)"
                ></vx-input>
            </div>

            <div class="vx-flex vx-flex-col">
                <div class="vx-flex vx-items-center vx-gap-2">
                    <vx-checkbox
                        v-model="data.terms"
                        data-test="terms"
                        name="terms"
                        size="md"
                        :error="displayFirstError(v$.terms.$errors)"
                    ></vx-checkbox>
                    <span class="vx-flex vx-items-center vx-gap-2"
                        >I accept the
                        <a href="/terms" target="_blank"
                            >Terms of Service</a
                        ></span
                    >
                </div>
                <span
                    v-if="v$.terms.$errors?.length"
                    class="vx-mt-2 vx-px-0.5 vx-text-xs vx-text-rose-800"
                >
                    {{ displayFirstError(v$.terms.$errors) }}
                </span>
            </div>

            <div
                class="vx-flex vx-flex-col vx-gap-2 vx-mt-4 md:vx-flex-row-reverse"
            >
                    <vx-button
                        @click.prevent="submit()"
                        data-test="submit"
                        :loading="submitting"
                        class="md:vx-flex-grow"
                        color="primary"
                        size="lg"
                    >
                        Register
                        <font-awesome-icon
                            :icon="faArrowRight"
                        ></font-awesome-icon>
                    </vx-button>
            </div>
        </div>
    </auth-box>
</template>
<script setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import AuthBox from "../AuthBox.vue";
import {
    VxLabel,
    VxInput,
    VxButton,
    VxCheckbox,
} from "@voxie/frontend-components";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { ref, reactive } from "vue";
import useVuelidate from "@vuelidate/core";
import {
    required,
    email as emailRule,
    sameAs,
    maxLength,
    helpers,
    minLength,
} from "@vuelidate/validators";
import { displayFirstError } from "~/utils/validation";
import { computed } from "vue";
import { HTTP_UNPROCESSABLE_ENTITY } from "~/support/HttpStatuses";
import { useToasted } from "~/composables/useToasted";
import { authService } from "~/services";

const props = defineProps({
    errors: Array,
    email: String,
    invitation: String,
});

const toasted = useToasted();

const data = reactive({
    name: "",
    email: props.email || "",
    password: "",
    password_confirmation: "",
    terms: false,
    invitation: props.invitation,
});

const submitting = ref(false);

const v$ = useVuelidate(
    computed(() => ({
        name: { required, maxLength: maxLength(120) },
        email: { required, email: emailRule, maxLength: maxLength(255) },
        password: {
            required,
            minLength: minLength(12),
            maxLength: maxLength(64),
        },
        password_confirmation: {
            required,
            sameAs: helpers.withMessage(
                "The password confirmation does not match.",
                sameAs(data.password)
            ),
        },
        terms: {
            required,
            checked: helpers.withMessage(
                "The terms field is required.",
                sameAs(true)
            ),
        },
    })),
    data
);

const errors = ref(props.errors);

const submit = async () => {
    submitting.value = true;

    const isFormCorrect = await v$.value.$validate();

    if (!isFormCorrect) {
        submitting.value = false;
        return;
    }

    try {
        const response = await authService.register(data);

        window.location.href = response.data?.redirect || "/";
    } catch (error) {
        if (error?.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
            const serverErrors = error?.response?.data?.errors;
            if (serverErrors) {
                errors.value = Object.values(serverErrors).flat();
            }
        } else {
            console.error(error);
            toasted.global.platform_error();
        }
    } finally {
        submitting.value = false;
    }
};

/**
 * Clear all watch marks suspended account message
 * {@link vendor/voxie/spark-aurelius/resources/js/spark.js}
 */
Object.keys(window.sessionStorage)
    .filter((key) => {
        return /displayedNotActiveTeam/.test(key);
    })
    .forEach((key) => {
        window.sessionStorage.removeItem(key);
    });
</script>
